import { motion } from 'framer-motion';
import { Pencil, ToggleLeft, ToggleRight } from 'lucide-react';
import { theme } from '@/lib/theme';
import type { AdminService } from '@/types';

interface ServicesTableProps {
  services: AdminService[];
  loading: boolean;
  onEdit: (service: AdminService) => void;
  onToggleActive: (id: string, active: boolean) => void;
}

const rowVariants = {
  hidden: { opacity: 0, y: 6 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.25, delay: i * 0.04, ease: 'easeOut' },
  }),
};

export default function ServicesTable({ services, loading, onEdit, onToggleActive }: ServicesTableProps) {
  const thClass = 'text-left text-xs font-semibold uppercase tracking-wider px-4 py-3';
  const tdClass = 'px-4 py-3.5 text-sm align-middle';

  return (
    <div
      className="rounded-xl border overflow-hidden"
      style={{ backgroundColor: theme.colors.bgSurface, borderColor: theme.colors.border }}
    >
      {/* ── MOBILE CARD VIEW ─────────────────────────────────── */}
      <div className="md:hidden divide-y" style={{ borderColor: theme.colors.border }}>
        {loading && [...Array(3)].map((_, i) => (
          <div key={i} className="p-4 animate-pulse space-y-2.5">
            <div className="flex justify-between">
              <div className="h-4 bg-white/8 rounded w-32" />
              <div className="h-5 bg-white/8 rounded-full w-16" />
            </div>
            <div className="h-3 bg-white/5 rounded w-full" />
            <div className="flex gap-3">
              <div className="h-3.5 bg-white/6 rounded w-20" />
              <div className="h-3.5 bg-white/6 rounded w-16" />
            </div>
          </div>
        ))}

        {!loading && services.length === 0 && (
          <div className="py-14 text-center">
            <div className="flex flex-col items-center gap-2">
              <i className="ri-scissors-line text-3xl" style={{ color: theme.colors.textDisabled }} />
              <p className="text-sm" style={{ color: theme.colors.textDisabled }}>No hay servicios registrados</p>
            </div>
          </div>
        )}

        {!loading && services.map((service, i) => (
          <div key={service.id} className="p-4 space-y-2.5">
            {/* Row 1: name + status */}
            <div className="flex items-center justify-between gap-2">
              <span className="font-semibold text-white text-sm">{service.name}</span>
              <span
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium flex-shrink-0"
                style={service.active ? { backgroundColor: 'rgba(52,211,153,0.12)', color: '#34d399' } : { backgroundColor: 'rgba(255,255,255,0.06)', color: theme.colors.textDisabled }}
              >
                <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: service.active ? '#34d399' : theme.colors.textDisabled }} />
                {service.active ? 'Activo' : 'Inactivo'}
              </span>
            </div>

            {/* Row 2: description */}
            {(service.short_description || service.landing_description) && (
              <p className="text-xs line-clamp-2" style={{ color: theme.colors.textMuted }}>
                {service.short_description || service.landing_description}
              </p>
            )}

            {/* Row 3: price + duration + actions */}
            <div className="flex items-center justify-between gap-2 pt-1">
              <div className="flex items-center gap-3">
                <span className="text-sm font-semibold" style={{ color: theme.colors.primary }}>
                  ${Number(service.price).toLocaleString('es-CO')}
                </span>
                <span className="text-xs" style={{ color: theme.colors.textSecondary }}>
                  {service.duration_minutes} min
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => onEdit(service)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg border transition-all cursor-pointer"
                  style={{ borderColor: theme.colors.border, color: theme.colors.textMuted }}
                  title="Editar"
                >
                  <Pencil size={13} />
                </button>
                <button
                  onClick={() => onToggleActive(service.id, !service.active)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg border transition-all cursor-pointer"
                  style={{ borderColor: theme.colors.border, color: service.active ? '#34d399' : theme.colors.textDisabled }}
                  title={service.active ? 'Desactivar' : 'Activar'}
                >
                  {service.active ? <ToggleRight size={15} /> : <ToggleLeft size={15} />}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ── DESKTOP TABLE VIEW ───────────────────────────────── */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr style={{ borderBottom: `1px solid ${theme.colors.border}` }}>
              <th className={thClass} style={{ color: theme.colors.textDisabled }}>Servicio</th>
              <th className={thClass} style={{ color: theme.colors.textDisabled }}>Descripción</th>
              <th className={thClass} style={{ color: theme.colors.textDisabled }}>Precio</th>
              <th className={thClass} style={{ color: theme.colors.textDisabled }}>Duración</th>
              <th className={thClass} style={{ color: theme.colors.textDisabled }}>Estado</th>
              <th className={thClass} style={{ color: theme.colors.textDisabled }}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading && [...Array(4)].map((_, i) => (
              <tr key={i} style={{ borderBottom: `1px solid ${theme.colors.border}` }}>
                {[...Array(6)].map((__, j) => (
                  <td key={j} className={tdClass}>
                    <div className="h-4 rounded animate-pulse" style={{ backgroundColor: 'rgba(255,255,255,0.06)', width: j === 1 ? '80%' : '60%' }} />
                  </td>
                ))}
              </tr>
            ))}

            {!loading && services.length === 0 && (
              <tr>
                <td colSpan={6} className="text-center py-12">
                  <div className="flex flex-col items-center gap-2">
                    <i className="ri-scissors-line text-3xl" style={{ color: theme.colors.textDisabled }} />
                    <p className="text-sm" style={{ color: theme.colors.textDisabled }}>No hay servicios registrados</p>
                  </div>
                </td>
              </tr>
            )}

            {!loading && services.map((service, i) => (
              <motion.tr
                key={service.id}
                custom={i}
                variants={rowVariants}
                initial="hidden"
                animate="visible"
                className="group transition-colors"
                style={{ borderBottom: `1px solid ${theme.colors.border}` }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLTableRowElement).style.backgroundColor = 'rgba(255,255,255,0.025)'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLTableRowElement).style.backgroundColor = 'transparent'; }}
              >
                <td className={tdClass}>
                  <span className="font-medium text-white">{service.name}</span>
                </td>
                <td className={tdClass} style={{ maxWidth: '280px' }}>
                  <div className="flex flex-col gap-1.5">
                    {service.short_description && (
                      <div>
                        <span className="text-[10px] uppercase tracking-wider font-semibold block mb-0.5" style={{ color: theme.colors.textDisabled }}>Booking</span>
                        <span className="text-xs leading-relaxed line-clamp-1" style={{ color: theme.colors.textMuted }} title={service.short_description}>{service.short_description}</span>
                      </div>
                    )}
                    {service.landing_description && (
                      <div>
                        <span className="text-[10px] uppercase tracking-wider font-semibold block mb-0.5" style={{ color: theme.colors.textDisabled }}>Landing</span>
                        <span className="text-xs leading-relaxed line-clamp-2" style={{ color: theme.colors.textMuted }} title={service.landing_description}>{service.landing_description}</span>
                      </div>
                    )}
                    {!service.short_description && !service.landing_description && (
                      <span className="text-xs" style={{ color: theme.colors.textDisabled }}>—</span>
                    )}
                  </div>
                </td>
                <td className={tdClass}>
                  <span className="font-semibold" style={{ color: theme.colors.primary }}>${Number(service.price).toLocaleString('es-CO')}</span>
                </td>
                <td className={tdClass}>
                  <span style={{ color: theme.colors.textSecondary }}>{service.duration_minutes} min</span>
                </td>
                <td className={tdClass}>
                  <span
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
                    style={service.active ? { backgroundColor: 'rgba(52,211,153,0.12)', color: '#34d399' } : { backgroundColor: 'rgba(255,255,255,0.06)', color: theme.colors.textDisabled }}
                  >
                    <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: service.active ? '#34d399' : theme.colors.textDisabled }} />
                    {service.active ? 'Activo' : 'Inactivo'}
                  </span>
                </td>
                <td className={tdClass}>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => onEdit(service)}
                      className="w-8 h-8 flex items-center justify-center rounded-lg border transition-all cursor-pointer hover:border-[#c9a84c]/40 hover:text-[#c9a84c]"
                      style={{ borderColor: theme.colors.border, color: theme.colors.textMuted, backgroundColor: 'transparent' }}
                      title="Editar"
                    >
                      <Pencil size={13} />
                    </button>
                    <button
                      onClick={() => onToggleActive(service.id, !service.active)}
                      className="w-8 h-8 flex items-center justify-center rounded-lg border transition-all cursor-pointer"
                      style={{ borderColor: theme.colors.border, color: service.active ? '#34d399' : theme.colors.textDisabled, backgroundColor: 'transparent' }}
                      title={service.active ? 'Desactivar' : 'Activar'}
                    >
                      {service.active ? <ToggleRight size={15} /> : <ToggleLeft size={15} />}
                    </button>
                  </div>
                </td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>{/* end hidden md:block */}
    </div>
  );
}
